import { EditIcon } from '~components/icons'
import type { Breakpoint } from '~hooks/use-breakpoint'
import { Box, Typography } from '@mui/material'
import { Step } from 'react-joyride'
import { Link } from 'react-router-dom'
import { PageRoute } from 'src/routes/routes-behind-login/page-route'
import { CargoListPageSearchParams } from '~pages/pages-behind-login/chartering/cargo-list-page/constants'
import { lastStep } from './common-steps'

const fullSizeTourBreakpoints: Breakpoint[] = ['xxxxxl']
const smallSizeTourBreakpoints: Breakpoint[] = ['sm', 'xs', 'md']

export function constructCargoListTour({
  breakpoint,
  hasUpdateScope,
}: {
  breakpoint: Breakpoint
  hasUpdateScope?: boolean
}): Step[] {
  const isFullSizeTour = fullSizeTourBreakpoints.includes(breakpoint)
  const isSmallSizeTour = smallSizeTourBreakpoints.includes(breakpoint)

  const updateCargoSteps: Step[] = [
    {
      content: (
        <>
          <Typography variant="label" sx={{ mb: 3 }} component="p">
            To add a cargo
          </Typography>
          <Typography variant="paragraph2">
            To add a new cargo, press “a” on your keyboard or click the “Add
            Cargo” button.
          </Typography>
        </>
      ),
      target: '.tour-step-open-add-cargo-modal-button',
      data: {
        next: `/${PageRoute.CHARTERING}/cargo-list?${CargoListPageSearchParams.createCargo}=true`,
        previous: `/${PageRoute.CHARTERING}/cargo-list`,
      },
    },
    {
      content: (
        <>
          <Typography variant="label" sx={{ mb: 3 }} component="p">
            Fill in cargo information
          </Typography>
          <Typography variant="paragraph2" component="p">
            To add a new cargo, fill in the mandatory fields such as Type, P&C,
            Pool, Laycan, Cargo Grade and Charterer.
          </Typography>
          <Typography variant="paragraph2" component="p" sx={{ mt: 2 }}>
            Fill in the optional fields if known or come back when you want to
            edit.
          </Typography>
        </>
      ),
      target: 'body',
      placement: 'center',
      data: {
        previous: `/${PageRoute.CHARTERING}/cargo-list`,
        next: `/${PageRoute.CHARTERING}/cargo-list`,
      },
    },
    {
      content: (
        <>
          <Typography variant="label" sx={{ mb: 3 }} component="p">
            Edit a cargo
          </Typography>
          <Typography variant="paragraph2" component="p">
            To edit a cargo, click the “pen” <EditIcon /> icon at the end of
            each line on the list.
          </Typography>
          {!isFullSizeTour && (
            <Typography variant="paragraph2" component="p" sx={{ mt: 2 }}>
              Note that you can scroll to the right to see all columns of the
              table.
            </Typography>
          )}
        </>
      ),
      target: '.tour-step-edit-cargo-button',
      data: {
        previous: `/${PageRoute.CHARTERING}/cargo-list?${CargoListPageSearchParams.createCargo}=true`,
        next: `/${PageRoute.CHARTERING}/cargo-list`,
      },
    },
    {
      content: (
        <>
          <Typography variant="label" sx={{ mb: 3 }} component="p">
            Quick update
          </Typography>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
              justifyContent: 'center',
            }}
          >
            <Typography variant="paragraph2">
              A shortcut to change the cargo status - simply click the arrow and
              select a new one.
            </Typography>

            <img
              src="https://strmtnoeupvesseltruth01.blob.core.windows.net/public/InSite/cargo-list-tour-quick-select.gif"
              title="quick select gif"
            />
          </Box>
        </>
      ),
      target: '.tour-step-status-cell',
      placement: 'top',
    },
  ]

  const defaultCargoListTour: Step[] = [
    {
      content: (
        <>
          <Typography variant="label" sx={{ mb: 3 }} component="p">
            Welcome to the Cargo List
          </Typography>
          <Typography variant="paragraph2" component="p">
            To help you get onboarded, we have made this small guided tour.
          </Typography>
          <Typography variant="paragraph2" component="p" sx={{ mt: 2 }}>
            You can skip the tour at any point, and you can always start it
            again from the <Link to="/help">Help page</Link>.
          </Typography>
        </>
      ),
      target: 'body',
      placement: 'center',
    },
    {
      content: (
        <>
          <Typography variant="label" sx={{ mb: 3 }} component="p">
            List content
          </Typography>
          <Typography variant="paragraph2">
            The quotes in the list are divided into 3 groups, based on which
            status the cargo is in:
            <ul>
              <li>Open / Poss (possibility) / On Hold</li>
              <li>On Subs</li>
              <li>Fixed, Failed and Withdrawn</li>
            </ul>
          </Typography>
        </>
      ),
      target: '.tour-step-table-section-open-poss',
    },
    {
      content: (
        <>
          <Typography variant="label" sx={{ mb: 3 }} component="p">
            Filtering
          </Typography>
          <Typography variant="paragraph2">
            Use filters to specify the content of your Cargo List.
          </Typography>
        </>
      ),
      target: '.tour-step-cargo-filters',
    },

    ...(hasUpdateScope ? updateCargoSteps : []),
    {
      content: (
        <>
          <Typography variant="label" sx={{ mb: 3 }} component="p">
            Hover
          </Typography>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
              justifyContent: 'center',
            }}
          >
            <Typography variant="paragraph2">
              Hover over a value to see more: the full text when it is too long
              to fit and extra details on columns such as Vessel, PIC and
              Comments.
            </Typography>
          </Box>
        </>
      ),
      target: '.tour-step-cargo-hover-cell',
      placement: 'top',
    },
    {
      content: (
        <>
          <Typography variant="label" sx={{ mb: 3 }} component="p">
            Date filter
          </Typography>
          <Typography variant="paragraph2" component="p">
            Use the date filter to specify the timeframe of the cargoes in your
            Cargo List.
          </Typography>
          <Typography variant="paragraph2" component="p" sx={{ mt: 2 }}>
            Note: this filter only applies to Fixed, Failed and Withdrawn
            cargoes.
          </Typography>
        </>
      ),
      target: '.tour-step-date-filters',
    },
    lastStep,
  ]

  if (!isSmallSizeTour)
    return defaultCargoListTour.map((step) => ({
      ...step,
      hideCloseButton: true,
      disableCloseOnEsc: true,
    }))

  return defaultCargoListTour.map((step) => ({
    ...step,
    target: 'body',
    placement: 'center',
    hideCloseButton: true,
    disableCloseOnEsc: true,
  }))
}
